import { useState, forwardRef, Children, isValidElement, useMemo, useEffect } from "react"
import { CaretDownIcon, CaretUpIcon } from "@modulz/radix-icons"
import { useNavigation } from "./Root"
import { Flex } from "../Flex"
import { CSSFn } from "../styles"
import GroupTrigger from "./styles/GroupTriggerStyle"

type GroupOwnProps = Readonly<{
  name: string
  css?: CSSFn
  defaultOpen?: boolean
}>

type GroupIconProps = Readonly<{
  width: string
  height: string
}>

export type GroupProps = React.ComponentPropsWithRef<typeof Flex> & GroupOwnProps

export const Group: React.FC<GroupProps> = forwardRef(
  ({ children, name, defaultOpen = false, ...containerProps }: GroupProps, forwardedRef) => {
    const iconProps: GroupIconProps = useMemo(
      () => ({
        width: `25px`,
        height: `25px`,
      }),
      []
    )
    const [isOpen, setIsOpen] = useState(defaultOpen)
    const [iconComponent, setIconComponent] = useState<JSX.Element>(<CaretUpIcon {...iconProps} />)

    const { state } = useNavigation(`Group`)

    const getChildrenValues = () => {
      const values = Children.map(children, (child) =>
        isValidElement(child) && typeof child.props.value === `string`
          ? child.props.value === `/`
            ? child.props.value
            : `/${child.props.value}`
          : undefined
      )

      return values?.filter((child): child is string => child !== undefined) ?? []
    }

    const isActive = getChildrenValues().includes(state.value)

    useEffect(() => {
      setIconComponent(() => (isOpen ? <CaretUpIcon {...iconProps} /> : <CaretDownIcon {...iconProps} />))
    }, [isOpen, iconProps])

    return (
      <Flex flexDirection="column" ref={forwardedRef} {...containerProps}>
        <GroupTrigger
          data-active={isActive ? `active` : `inactive`}
          onClick={() => setIsOpen((currentState) => !currentState)}
        >
          {name}
          {iconComponent}
        </GroupTrigger>

        {isOpen && <Flex flexDirection="column">{children}</Flex>}
      </Flex>
    )
  }
)
